import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Image as ImageIcon,
  Film,
  Layers,
  Sliders,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  SeparatorHorizontal,
} from "lucide-react";
import {
  mediaContentSchema,
  type MediaContent,
  type MediaItem,
} from "@profolio/validators";

interface MediaEditorProps {
  content: Record<string, unknown>;
  onChange: (content: MediaContent) => void;
}

export function MediaEditor({ content, onChange }: MediaEditorProps) {
  const [detailsOpen, setDetailsOpen] = useState(true);

  const parseResult = mediaContentSchema.safeParse(content);
  const data: MediaContent = parseResult.success
    ? parseResult.data
    : {
        heading: "",
        description: "",
        mediaType: "image",
        videoUrl: "",
        items: [],
        aspectRatio: "auto",
        galleryLayout: "grid",
        alignment: "center",
        spacerHeight: "md",
      };

  const {
    heading = "",
    description = "",
    mediaType = "image",
    videoUrl = "",
    items = [],
    aspectRatio = "auto",
    galleryLayout = "grid",
    alignment = "center",
    spacerHeight = "md",
  } = data;

  const update = (partial: Partial<MediaContent>) => {
    onChange({
      ...data,
      ...partial,
    });
  };

  const addItem = () => {
    if (items.length >= 20) return;
    const newItem: MediaItem = {
      id: "m_" + Math.random().toString(36).substring(2, 9),
      url: "",
      alt: "",
      caption: "",
      linkUrl: "",
    };
    update({ items: [...items, newItem] });
  };

  const updateItem = (index: number, partial: Partial<MediaItem>) => {
    const nextItems = [...items];
    nextItems[index] = { ...nextItems[index], ...partial };
    update({ items: nextItems });
  };

  const removeItem = (index: number) => {
    update({ items: items.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-6">
      {/* ── 1. Heading & Description ── */}
      <div className="space-y-4">
        <div>
          <Label htmlFor="media-heading" className="text-xs font-semibold">
            Section Heading (Optional)
          </Label>
          <Input
            id="media-heading"
            value={heading}
            onChange={(e) => update({ heading: e.target.value })}
            placeholder="e.g., Visual Highlights, Keynote Gallery, or leave empty"
            className="mt-1 text-xs"
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Leave blank if you want a seamless visual block or spacer between sections.
          </p>
        </div>

        <div>
          <Label htmlFor="media-description" className="text-xs font-semibold">
            Description / Subtitle (Optional)
          </Label>
          <Textarea
            id="media-description"
            value={description}
            onChange={(e) => update({ description: e.target.value })}
            placeholder="Brief intro or context for the visuals below..."
            rows={2}
            className="mt-1 text-xs"
          />
        </div>
      </div>

      {/* ── 2. Media Type Selection ── */}
      <div>
        <Label className="text-xs font-semibold mb-2 block">Media Display Mode</Label>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {[
            { id: "image", label: "Single Image", icon: ImageIcon },
            { id: "image-gallery", label: "Gallery", icon: Layers },
            { id: "carousel", label: "Touch Carousel", icon: Sliders },
            { id: "video", label: "Video Embed", icon: Film },
            { id: "spacer", label: "Blank Spacer", icon: SeparatorHorizontal },
          ].map((mode) => {
            const Icon = mode.icon;
            const isSelected = mediaType === mode.id;
            return (
              <button
                key={mode.id}
                type="button"
                onClick={() => update({ mediaType: mode.id as MediaContent["mediaType"] })}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border text-center transition-all cursor-pointer ${
                  isSelected
                    ? "bg-primary/10 border-primary text-primary font-semibold shadow-xs"
                    : "bg-card hover:bg-muted/40 border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className="h-5 w-5 mb-1.5" />
                <span className="text-xs">{mode.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── 3. Blank Spacer Controls ── */}
      {mediaType === "spacer" && (
        <div className="rounded-xl border bg-card/60 p-4 space-y-3">
          <Label className="text-xs font-semibold block">Spacer Vertical Height</Label>
          <div className="flex gap-2">
            {[
              { id: "sm", label: "Small (24px)" },
              { id: "md", label: "Medium (48px)" },
              { id: "lg", label: "Large (80px)" },
              { id: "xl", label: "Extra Large (120px)" },
            ].map((sp) => (
              <button
                key={sp.id}
                type="button"
                onClick={() => update({ spacerHeight: sp.id as "sm" | "md" | "lg" | "xl" })}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-all cursor-pointer ${
                  (spacerHeight || "md") === sp.id
                    ? "bg-primary text-primary-foreground font-semibold"
                    : "bg-card hover:bg-muted/40 text-muted-foreground"
                }`}
              >
                {sp.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground">
            Adds clean vertical breathing room between your profile sections without rendering any media containers.
          </p>
        </div>
      )}

      {/* ── 4. Video Embed Controls ── */}
      {mediaType === "video" && (
        <div className="rounded-xl border bg-card/60 p-4 space-y-4">
          <div>
            <Label htmlFor="media-video-url" className="text-xs font-semibold">
              Video URL (YouTube, Vimeo, or MP4)
            </Label>
            <Input
              id="media-video-url"
              value={videoUrl}
              onChange={(e) => update({ videoUrl: e.target.value })}
              placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
              className="mt-1 text-xs"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Paste full YouTube or Vimeo link. It will automatically convert to a responsive embed.
            </p>
          </div>

          <div>
            <Label className="text-xs font-semibold mb-1.5 block">Aspect Ratio</Label>
            <div className="flex gap-2">
              {[
                { id: "16-9", label: "16:9 (Widescreen)" },
                { id: "4-3", label: "4:3 (Standard)" },
                { id: "1-1", label: "1:1 (Square)" },
              ].map((asp) => (
                <button
                  key={asp.id}
                  type="button"
                  onClick={() => update({ aspectRatio: asp.id as MediaContent["aspectRatio"] })}
                  className={`px-3 py-1.5 text-xs rounded-lg border transition-all cursor-pointer ${
                    aspectRatio === asp.id
                      ? "bg-primary text-primary-foreground font-semibold"
                      : "bg-card hover:bg-muted/40 text-muted-foreground"
                  }`}
                >
                  {asp.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── 5. Image, Gallery & Carousel Controls ── */}
      {mediaType !== "video" && mediaType !== "spacer" && (
        <div className="space-y-4">
          {/* Gallery / Carousel Options */}
          <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-muted/20 border rounded-xl">
            {mediaType === "image-gallery" && (
              <div className="flex items-center gap-2">
                <Label className="text-xs font-semibold">Layout:</Label>
                <div className="flex gap-1">
                  {[
                    { id: "grid", label: "2-Col Grid" },
                    { id: "stacked", label: "Stacked" },
                    { id: "row", label: "Horizontal Scroll" },
                  ].map((lay) => (
                    <button
                      key={lay.id}
                      type="button"
                      onClick={() =>
                        update({ galleryLayout: lay.id as MediaContent["galleryLayout"] })
                      }
                      className={`px-2.5 py-1 text-xs rounded-md border transition-all cursor-pointer ${
                        galleryLayout === lay.id
                          ? "bg-primary text-primary-foreground font-semibold"
                          : "bg-card hover:bg-muted/40 text-muted-foreground"
                      }`}
                    >
                      {lay.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Label className="text-xs font-semibold">Aspect Ratio:</Label>
              <div className="flex gap-1">
                {[
                  { id: "auto", label: "Original" },
                  { id: "16-9", label: "16:9" },
                  { id: "4-3", label: "4:3" },
                  { id: "1-1", label: "1:1" },
                ].map((asp) => (
                  <button
                    key={asp.id}
                    type="button"
                    onClick={() => update({ aspectRatio: asp.id as MediaContent["aspectRatio"] })}
                    className={`px-2.5 py-1 text-xs rounded-md border transition-all cursor-pointer ${
                      aspectRatio === asp.id
                        ? "bg-primary text-primary-foreground font-semibold"
                        : "bg-card hover:bg-muted/40 text-muted-foreground"
                    }`}
                  >
                    {asp.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs font-semibold">Align:</Label>
              <div className="flex gap-1">
                {[
                  { id: "left", label: "Left" },
                  { id: "center", label: "Center" },
                  { id: "right", label: "Right" },
                ].map((aln) => (
                  <button
                    key={aln.id}
                    type="button"
                    onClick={() => update({ alignment: aln.id as MediaContent["alignment"] })}
                    className={`px-2.5 py-1 text-xs rounded-md border transition-all cursor-pointer ${
                      alignment === aln.id
                        ? "bg-primary text-primary-foreground font-semibold"
                        : "bg-card hover:bg-muted/40 text-muted-foreground"
                    }`}
                  >
                    {aln.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Items List */}
          <div className="rounded-xl border bg-card/60 overflow-hidden">
            <button
              type="button"
              onClick={() => setDetailsOpen(!detailsOpen)}
              className="flex w-full items-center justify-between p-3.5 text-xs font-semibold hover:bg-muted/40 transition-colors"
            >
              <div className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4 text-primary" />
                <span>
                  {mediaType === "image" ? "Image Details" : `Media Items (${items.length})`}
                </span>
              </div>
              {detailsOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>

            {detailsOpen && (
              <div className="p-4 pt-2 border-t space-y-4">
                {items.length === 0 ? (
                  <div className="rounded-xl border border-dashed p-6 text-center text-xs text-muted-foreground space-y-2">
                    <ImageIcon className="h-8 w-8 mx-auto text-muted-foreground/50" />
                    <div>No images added yet. Click below to add your first image.</div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {items.map((item, index) => (
                      <div
                        key={item.id || index}
                        className="p-3.5 rounded-xl border bg-background/80 space-y-3 relative group"
                      >
                        <div className="flex items-center justify-between gap-2 border-b pb-2">
                          <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                            Item #{index + 1}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeItem(index)}
                            className="text-destructive hover:bg-destructive/10 h-7 px-2 text-xs"
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                          </Button>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <Label className="text-[11px] font-semibold block">
                                Image URL <span className="text-destructive">*</span>
                              </Label>
                              {(!item.url || item.url.trim() === "") && (
                                <span className="text-[10px] text-amber-600 dark:text-amber-400 bg-amber-500/10 px-1.5 py-0.2 rounded border border-amber-500/20 font-medium">
                                  Draft empty (hidden from live site)
                                </span>
                              )}
                            </div>
                            <Input
                              value={item.url}
                              onChange={(e) => updateItem(index, { url: e.target.value })}
                              placeholder="https://images.unsplash.com/..."
                              className="text-xs"
                            />
                            {item.url && !item.url.startsWith("http://") && !item.url.startsWith("https://") && !item.url.startsWith("/") && (
                              <span className="text-[10px] text-destructive block mt-1">
                                Must be a valid web URL starting with https:// or http://
                              </span>
                            )}
                          </div>

                          <div>
                            <Label className="text-[11px] font-semibold mb-1 block">
                              Alt Text / Accessible Description
                            </Label>
                            <Input
                              value={item.alt || ""}
                              onChange={(e) => updateItem(index, { alt: e.target.value })}
                              placeholder="Describe image for screen readers"
                              className="text-xs"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-[11px] font-semibold mb-1 block">
                              Caption (Optional)
                            </Label>
                            <Input
                              value={item.caption || ""}
                              onChange={(e) => updateItem(index, { caption: e.target.value })}
                              placeholder="Short caption shown under image"
                              className="text-xs"
                            />
                          </div>

                          <div>
                            <Label className="text-[11px] font-semibold mb-1 block">
                              Click-Through Link URL (Optional)
                            </Label>
                            <Input
                              value={item.linkUrl || ""}
                              onChange={(e) => updateItem(index, { linkUrl: e.target.value })}
                              placeholder="https://example.com/project"
                              className="text-xs"
                            />
                          </div>
                        </div>

                        {/* Thumbnail Preview */}
                        {item.url && (
                          <div className="mt-2 flex items-center gap-3 p-2 rounded-lg bg-muted/30 border">
                            <div className="h-12 w-16 rounded overflow-hidden bg-muted shrink-0 border">
                              <img
                                src={item.url}
                                alt={item.alt || ""}
                                className="h-full w-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLElement).style.display = "none";
                                }}
                              />
                            </div>
                            <div className="text-[11px] text-muted-foreground truncate flex-1">
                              Preview: {item.caption || item.alt || item.url}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {(mediaType !== "image" || items.length === 0) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addItem}
                    disabled={items.length >= 20}
                    className="gap-1.5 text-xs h-8 cursor-pointer"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {items.length === 0 ? "Add Image" : "Add Another Image"}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
